import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { FraudCaseStatus } from '@mynt/backoffice-api-spec/fraud'
import { useState } from 'react'
import type { ListFraudCasesParams } from '../types'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isValidUuid = (value: string) => UUID_REGEX.test(value)

const DEFAULT_STATUS_FILTER: string[] = ['NEW', 'NOTIFIED']

const statusOptions = Object.values(FraudCaseStatus).map((value) => ({ label: value, value }))

type IdField = { key: keyof ListFraudCasesParams; label: string; requiresUuid: boolean }

const ALL_ID_FIELDS: IdField[] = [
  { key: 'case-id', label: 'Case ID', requiresUuid: true },
  { key: 'customer-id', label: 'Customer ID', requiresUuid: true },
  { key: 'card-id', label: 'Card ID', requiresUuid: true },
  { key: 'person-id', label: 'Person ID', requiresUuid: true },
  { key: 'external-id', label: 'External ID', requiresUuid: true },
]

export type FilterFieldKey = 'case-id' | 'customer-id' | 'card-id' | 'person-id' | 'external-id'

type FraudCasesFiltersProps = {
  fields: FilterFieldKey[]
  defaultStatus?: string[]
  initialFieldValues?: Record<string, string>
  onReset?: () => void
  onSearch: (filters: Partial<ListFraudCasesParams>) => void
}

export const FraudCasesFilters = ({
  fields,
  defaultStatus = DEFAULT_STATUS_FILTER,
  initialFieldValues,
  onReset,
  onSearch,
}: FraudCasesFiltersProps) => {
  const [statusFilter, setStatusFilter] = useState<string[]>(defaultStatus)
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(initialFieldValues ?? {})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const visibleFields = ALL_ID_FIELDS.filter((f) => fields.includes(f.key as FilterFieldKey))

  const applySearch = () => {
    const errors: Record<string, string> = {}
    const filters: Record<string, string> = {}

    for (const field of visibleFields) {
      const value = fieldValues[field.key]?.trim()
      if (value) {
        if (field.requiresUuid && !isValidUuid(value)) {
          errors[field.key] = 'Must be a valid UUID'
        } else {
          filters[field.key] = value
        }
      }
    }

    setFieldErrors(errors)
    if (Object.keys(errors).length === 0) {
      onSearch({ status: statusFilter as ListFraudCasesParams['status'], ...filters })
    }
  }

  const handleReset = () => {
    if (onReset) {
      onReset()
      return
    }
    setStatusFilter(defaultStatus)
    setFieldValues({})
    setFieldErrors({})
    onSearch({ status: defaultStatus as ListFraudCasesParams['status'] })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      applySearch()
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, flex: 1, maxWidth: 800 }}>
        <Autocomplete
          multiple
          size="small"
          options={statusOptions}
          value={statusOptions.filter((o) => statusFilter.includes(o.value))}
          onChange={(_, options) => setStatusFilter(options.map((o) => o.value))}
          renderInput={(params) => <TextField {...params} label="Status" />}
          sx={{ '& .MuiInputBase-root': { height: 40, flexWrap: 'nowrap', overflow: 'hidden' } }}
        />
        {visibleFields.map((field) => (
          <TextField
            key={field.key}
            label={field.label}
            size="small"
            value={fieldValues[field.key] ?? ''}
            onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder={field.requiresUuid ? 'UUID' : ''}
            error={Boolean(fieldErrors[field.key])}
            helperText={fieldErrors[field.key]}
            slotProps={{ formHelperText: { sx: { position: 'absolute', bottom: -22 } } }}
            sx={{ position: 'relative' }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: '4px' }}>
        <Button variant="contained" onClick={applySearch} sx={{ height: 40 }}>
          Search
        </Button>
        <Button variant="outlined" onClick={handleReset} sx={{ height: 40 }}>
          Reset
        </Button>
      </Box>
    </Box>
  )
}
